import Phaser from 'phaser';
import ScreenContainer from '../display/ui/ScreenContainer';
import Hud from 'app/ui/hud/Hud';
import {
  LOCATIONS,
  TUTORIAL_WORLDMAP,
  LOCATIONS_VISIT_ORDER,
  FONT_TITLE,
  FONT_COPY,
} from '../constants/AppConstants';
import TutorialOverlay from '../ui/tutorial/TutorialOverlay';
import ScreenOverlayStickerEarned from '../ui/overlays/ScreenOverlayStickerEarned';
import ScreenOverlayCertificate from '../ui/overlays/ScreenOverlayCertificate';
import ConfettiDrop from '../ui/confetti/ConfettiDrop';
import EndGameOverlay from '../ui/endGame/EndGameOverlay';

export default class extends Phaser.State {
  constructor() {
    super();
    this.isZoomedIn = false;
    this.mapSprite = null;
    this.mapContainer = null;

    // Tween Options -- Probably move in to ui.json later
    this.tween = {
      time: 300,
      ease: Phaser.Easing.Linear.None,
    };

    this.pi = 0;
    this.path = [];
    this.triggerTransition = false;

    this.nextLocation = '';
    this.visitedLocations = '';
    this.currentLocation = '';
  }

  init() {}

  preload() {}

  create() {
    this.visitedLocations = this.game.gameController.player.getVisitedLocations();
    this.currentLocation = this.game.gameController.player.getCurrentLocation();
    this.storyLocation = this.game.gameController.player.getCurrentStoryLocation();

    // store everything in a container, we will reposition this for screen
    this.container = new ScreenContainer(this.game, { background: false });

    // Create a group for map and icons. might be better this why
    // so it can all be scaled correctly later
    this.mapContainer = this.game.make.group();

    // Setup the mapContainer assets
    this.mapSprite = this.game.make.sprite(0, 0, 'background_world_map');
    this.mapContainer.add(this.mapSprite);
    this.mapContainer.x = (this.game.width - this.game.defaultWidth) / 2;
    this.mapContainer.y = (this.game.height - this.game.defaultHeight) / 2;
    this.mapContainer.add(this.createPlaceholderButtons());

    // Overlay
    this.bgOverlay = this.game.add.image(0, 0, 'background_overlay');
    this.bgOverlay.anchor.set(0.5);
    this.bgOverlay.x = this.game.defaultWidth * 0.5;
    this.bgOverlay.y = this.game.defaultHeight * 0.5;
    this.mapOverlay = this.game.make.group();
    this.mapOverlay.fixedToCamera = true;
    this.mapOverlay.cameraOffset.x = (this.game.width - this.game.defaultWidth) / 2;
    this.mapOverlay.cameraOffset.y = (this.game.height - this.game.defaultHeight) / 2;
    this.mapOverlay.add(this.bgOverlay);

    // Setup Hud
    this.hud = new Hud(this.game, Hud.WORLDMAP);
    this.btnNext = this.hud.getButton('nextpage');
    this.btnPrev = this.hud.getButton('prevpage');
    this.hud.onButtonSelect.add(this.handleHudButton, this);
    this.game.add.existing(this.hud);

    // did the player earn a sticker? this will trigger the sticker overlay and the transition to next location
    const queuedSticker = this.game.gameController.player.getQueuedLocationSticker();

    this.hideMapIcons();
    if (queuedSticker) {
      this.stickerUnlockOverlay = new ScreenOverlayStickerEarned(this.game, {
        locationid: queuedSticker,
        suitcase: this.hud.getButton('suitcase'),
      });
      this.game.add.existing(this.stickerUnlockOverlay);
      this.stickerUnlockOverlay.onAnimationComplete.add(this.handleStickerEarned, this);
      this.hideCycleButtons();
    } else {
      this.updateCycleButtons();
      if (!this.game.gameController.player.getTutorialSeen(TUTORIAL_WORLDMAP)) {
        this.addWorldMapOnboarding();
      } else if (!queuedSticker) {
        this.zoomToLocation(this.game.gameController.player.getCurrentLocation(), 1000);
      }
    }

    // play music
    this.game.sounds.setBackgroundMusic('music_map', true);
  }

  handleHudButton(btnid) {
    switch (btnid) {
      case 'nextpage':
        this.handleNextLocation();
        break;
      case 'prevpage':
        this.handlePrevLocation();
        break;
    }
  }

  hideCycleButtons() {
    this.btnNext.visible = false;
    this.btnPrev.visible = false;
  }

  updateCycleButtons() {
    if (!this.isZoomedIn) {
      this.hideCycleButtons();
    } else {
      const currentIndex = LOCATIONS_VISIT_ORDER.indexOf(this.currentLocation);

      const storyIndex = LOCATIONS_VISIT_ORDER.indexOf(
        this.game.gameController.player.getCurrentStoryLocation()
      );

      this.btnNext.visible = currentIndex < storyIndex;
      this.btnPrev.visible = currentIndex > 0;
    }
  }

  handleNextLocation() {
    const currentIndex = LOCATIONS_VISIT_ORDER.indexOf(this.currentLocation);
    const targetLocation = LOCATIONS_VISIT_ORDER[currentIndex + 1];
    this.currentLocation = targetLocation;
    this.zoomToLocation(targetLocation);
    this.updateCycleButtons();
  }

  handlePrevLocation() {
    const currentIndex = LOCATIONS_VISIT_ORDER.indexOf(this.currentLocation);
    const targetLocation = LOCATIONS_VISIT_ORDER[currentIndex - 1];
    this.currentLocation = targetLocation;
    this.zoomToLocation(targetLocation);
    this.updateCycleButtons();
  }

  handleStickerEarned() {
    const queuedSticker = this.game.gameController.player.getQueuedLocationSticker();
    this.game.gameController.player.earnLocationSticker(queuedSticker);
    this.hud.getButton('suitcase').updateStickers();

    this.game.sounds.playSpriteSFX('sticker', { spriteID: 'general_audio' });

    // so if the player has eraned a certificate we need to show that before moving on
    // maybe some kinda overlay queue woulda been handy, but y'know... hindsight sucks
    if (queuedSticker === LOCATIONS_VISIT_ORDER[LOCATIONS_VISIT_ORDER.length - 1]) {
      // earn certificate
      this.game.gameController.player.setCertificateEarned();
      this.certificateOverlay = new ScreenOverlayCertificate(this.game);
      this.game.add.existing(this.certificateOverlay);
      this.certificateOverlay.onScreenClosed.add(this.displayEndGameOverlay, this);

      // Show confetti - YAY PRETTY!
      this.confetti = new ConfettiDrop(this.game);
      this.confettiDisplay = this.game.make.image(0, 0, this.confetti);
      this.confettiDisplay.fixedToCamera = true;
      this.game.add.existing(this.confettiDisplay);
    } else {
      this.handleStickerAndCertificateComplete();
    }
  }

  displayEndGameOverlay() {
    this.confetti = null;
    this.confettiDisplay.visible = false;
    this.confettiDisplay = null;
    this.endGameOverlay = new EndGameOverlay(this.game);
    this.game.add.existing(this.endGameOverlay);
    this.endGameOverlay.onScreenEnd.add(this.handleStickerAndCertificateComplete, this);
  }

  handleStickerAndCertificateComplete() {
    if (this.endGameOverlay) {
      this.endGameOverlay.visible = false;
    }
    // moved this to here - only want next location icon if transitioning to there, otherwise it shows extra icons
    this.nextLocation = this.game.gameController.player.getNextLocation();

    // Trigger location transition
    // how should i get next location?
    this.zoomToLocation(this.game.gameController.player.getCurrentLocation());

    if (this.nextLocation != '') {
      this.addTransitionAssets(this.game.gameController.player.getCurrentLocation());
    }
  }

  /*
  ==================================================================================
  Sprite Path Following
  ==================================================================================
  */

  addTransitionAssets(locationid) {
    const transitionSettings = this.game.ui_config.ui.map_icons_coordinates[locationid]
      .transition_coordinates;
    var py = transitionSettings.points.y;

    this.bmd = this.game.make.bitmapData(this.mapContainer.width, this.mapContainer.height);
    this.bmd.context.moveTo(transitionSettings.points.x[0], transitionSettings.points.y[0]);
    this.bmd.context.lineWidth = '5';
    this.bmd.context.strokeStyle = 'white';
    this.bmd.context.shadowColor = 'rgba(138, 138, 138, 0.1)';
    this.bmd.context.shadowBlur = 1;
    this.bmd.context.shadowOffsetX = 0;
    this.bmd.context.shadowOffsetY = 1;
    this.bmd.context.setLineDash([15, 8]);
    this.bitmapSprite = this.game.make.sprite(0, 0, this.bmd);
    this.mapContainer.add(this.bitmapSprite);

    this.mapArrowShadow = this.game.make.sprite(0, 0, 'map_arrow_shadow');
    this.mapContainer.add(this.mapArrowShadow);
    this.mapArrowShadow.x = transitionSettings.points.x[0];
    this.mapArrowShadow.y = transitionSettings.points.y[0] + 2;
    this.mapArrowShadow.anchor.set(0.5);

    this.mapArrow = this.game.make.sprite(0, 0, 'map_arrow');
    this.mapContainer.add(this.mapArrow);
    this.mapArrow.x = transitionSettings.points.x[0];
    this.mapArrow.y = transitionSettings.points.y[0];
    this.mapArrow.anchor.set(0.5);

    this.machine = this.game.make.sprite(0, 0, 'mystery_machine', transitionSettings.vehicleAsset);
    this.mapContainer.add(this.machine);
    this.machine.x = transitionSettings.points.x[0];
    this.machine.y = transitionSettings.points.y[0];
    this.machine.anchor.setTo(
      transitionSettings.vehicleAnchor.x,
      transitionSettings.vehicleAnchor.y
    );
    this.machine.scale.set(0);

    this.game.camera.follow(this.machine);

    // Trigger the plot path
    this.plotPath(transitionSettings);
  }

  plotPath(transitionSettings) {
    this.game.world.setBounds(
      transitionSettings.points.x,
      transitionSettings.points.y,
      this.mapContainer.width * 2,
      this.mapContainer.height * 2
    );

    this.triggerTransition = !this.triggerTransition;
    this.machineTweenHandle('out');
    this.bmd.clear();

    this.path = [];

    var ix = 0;
    var x = 10 / this.game.width;

    for (var i = 0; i <= 1; i += x) {
      var px = this.math.catmullRomInterpolation(transitionSettings.points.x, i);
      var py = this.math.catmullRomInterpolation(transitionSettings.points.y, i);

      var node = { x: px, y: py, angle: 0 };

      if (ix > 0) {
        node.angle = this.math.angleBetweenPoints(this.path[ix - 1], node);
      }

      this.path.push(node);

      ix++;
    }

    this.mapArrow.rotation = this.path[this.path.length - 1].angle;
    this.mapArrowShadow.rotation = this.path[this.path.length - 1].angle;
  }

  update() {
    if (this.triggerTransition) {
      this.machine.x = this.path[this.pi].x;
      this.machine.y = this.path[this.pi].y;

      this.mapArrow.x = this.path[this.pi].x;
      this.mapArrow.y = this.path[this.pi].y;

      this.mapArrowShadow.x = this.path[this.pi].x;
      this.mapArrowShadow.y = this.path[this.pi].y + 2;

      this.bmd.context.lineTo(this.machine.x, this.machine.y);
      this.bmd.context.stroke();

      this.pi++;

      if (this.pi >= this.path.length) {
        this.pi = 0;
        this.triggerTransition = false;
        this.machineTweenHandle();

        // machine has arrived, setting current location here for save reasons
        this.game.gameController.player.setCurrentLocation(this.nextLocation);
        // switching current and clearing next
        this.currentLocation = this.nextLocation;
        this.nextLocation = '';
        // update icons to hide previous location ( may not be wanted )
        this.updateMapIcons();
        this.updateCycleButtons();
      }
    }
    if (this.confetti) {
      // Show confetti - YAY PRETTY!
      this.confetti.updateConfetti();
    }
  }

  /*
  ==================================================================================
  ONBOARDING
  ==================================================================================
  */

  addWorldMapOnboarding() {
    const tutorial = [
      {
        copy: this.game.copy.getCopy('onboarding_worldmap_01'),
        image: 'tutorial_worldmap_01',
      },
    ];
    this.tutorial = new TutorialOverlay(this.game, tutorial, false);
    this.tutorial.onComplete.add(this.handleOnboardingComplete, this);
    this.game.gameController.player.setTutorialSeen(TUTORIAL_WORLDMAP);
    this.game.add.existing(this.tutorial);
  }

  handleOnboardingComplete() {
    if (this.tutorial) {
      this.tutorial.destroy(true);
      this.tutorial.onComplete.remove(this.handleOnboardingComplete, this);
      this.tutorial = null;
    }
    this.zoomToLocation(this.game.gameController.player.getCurrentLocation());
  }

  /*
  ==================================================================================
    USER IPUT
  ==================================================================================
  */

  mapIconClickHandler(btn, pointer, id) {
    if (!this.isZoomedIn) {
      this.zoomToLocation(id);
    } else {
      this.isZoomedIn = !this.isZoomedIn;
      this.game.gameController.triggerLocation(id);
    }
  }

  /*
  ===================================================
  Overlay
  ===================================================
  */

  setBgOverlay(background) {
    if (!this.bg) {
      this.bg = this.game.make.image(0, 0);
      this.bg.anchor.set(0.5);
      this.bg.x = this.game.defaultWidth * 0.5;
      this.bg.y = this.game.defaultHeight * 0.5;
      this.mapContainer.addChildAt(this.bg, 0);
    }
    this.bg.loadTexture(background);
  }

  /*
  ==================================================================================
    LOCATION ZOOM
  ==================================================================================
  */

  machineTweenHandle(scaleType) {
    this.game.add.tween(this.machine.scale).to(
      {
        x: scaleType == 'out' ? 0.5 : 0,
        y: scaleType == 'out' ? 0.5 : 0,
      },
      this.tween.time,
      this.tween.ease,
      true
    );
  }

  zoomToLocation(locationid, delay = 0) {
    // reset the camera if following machine
    if (this.game.camera.target) {
      this.game.world.setBounds(
        this.mapContainer.x,
        this.mapContainer.y,
        this.mapContainer.width,
        this.mapContainer.height
      );

      if (this.bitmapSprite) {
        this.bitmapSprite.visible = false;
        this.mapArrow.visible = false;
      }
      this.game.camera.follow(null);
      this.game.add
        .tween(this.game.camera)
        .to({ x: 0, y: 0 }, this.tween.time)
        .start();
    }

    const zoomedInMapCoor = this.game.ui_config.ui.map_icons_coordinates[locationid].zoomed_in
      .map_coordinates;

    this.zoomtween = this.game.add.tween(this.mapContainer.scale).to(
      {
        x: 2.5,
        y: 2.5,
      },
      this.tween.time,
      this.tween.ease,
      true,
      delay
    );

    this.zoomtween.onComplete.add(this.handleZoomComplete, this);

    this.game.add.tween(this.mapContainer).to(
      {
        x: zoomedInMapCoor.x,
        y: zoomedInMapCoor.y,
      },
      this.tween.time,
      this.tween.ease,
      true,
      delay
    );

    this.updateMapIcons(locationid);

    // this toggle was causing slight issues with the prev/next
    // looks like it can only be zoomed in at this stage ( scale : 2.5 )
    // so set to true, lemme know if I've missed something :)
    // this.isZoomedIn = !this.isZoomedIn;
    this.isZoomedIn = true;
  }
  handleZoomComplete() {
    this.zoomtween.onComplete.remove(this.handleZoomComplete, this);
    this.updateCycleButtons();
    this.updateMapIcons();
  }

  hideMapIcons() {
    this.mapContainer.children[1].children.forEach((icon) => {
      icon.alpha = 0;
    });
  }

  updateMapIcons(id) {
    this.mapContainer.children[1].children.forEach((icon) => {
      let targetAlpha = 1;

      if (
        !this.isZoomedIn ||
        (this.currentLocation != icon.locationid && this.nextLocation != icon.locationid)
      ) {
        targetAlpha = 0;
      }

      icon.inputEnabled = targetAlpha > 0;
      if (icon.inputEnabled) {
        icon.input.useHandCursor = true;
      }

      this.game.add.tween(icon).to(
        {
          alpha: targetAlpha,
        },
        this.tween.time,
        this.tween.ease,
        true
      );
    });
  }

  /*
  ================================================================
  // TEMP ::  create some placeholder location buttons for user flow tests
  // feel free to utilise or delete according to what you need :)
  ================================================================
  */
  createPlaceholderButtons() {
    const btnContainer = this.game.make.group();
    btnContainer.width = this.game.defaultWidth;
    btnContainer.x = 0;
    LOCATIONS.forEach((location, index) => {
      const btn = this.createButton(location, this.mapIconClickHandler);

      btnContainer.add(btn);
    });
    return btnContainer;
  }

  createButton(locationid, callback) {
    // Shortcut to map icon coordinates
    const zoomedOutCoor = this.game.ui_config.ui.map_icons_coordinates[locationid].icon_placement;
    const iconText = this.game.ui_config.ui.map_icons_coordinates[locationid].icon_text;

    const canVisit = this.game.gameController.player.getCanVisitLocation(locationid);
    const btn = this.game.make.sprite(0, 0, 'map_icons', locationid);

    btn.scale.set(0.75);
    btn.x = zoomedOutCoor.x;
    btn.y = zoomedOutCoor.y;
    this.label = this.game.make.text(
      0,
      0,
      this.game.copy.getCopy(locationid.toLowerCase() + '_long'),
      {
        font: '18px ' + this.game.titleFontStyle,
        fill: '#FFFFFF',
        wordWrap: true,
        wordWrapWidth: iconText.wrap_width,
        align: 'center',
      }
    );
    this.label.lineSpacing = -10;
    this.label.anchor.set(0.5);
    this.label.rotation = iconText.rotation;
    this.label.x = iconText.x;
    this.label.y = iconText.y;

    btn.addChild(this.label);

    if (canVisit) {
      btn.inputEnabled = true;
      btn.locationid = locationid;
      btn.input.useHandCursor = true;
      btn.events.onInputDown.add(callback, this, 0, locationid);
    }
    return btn;
  }

  shutdown() {
    // destroy
    this.container.destroy(true);

    if (this.confetti) {
      this.confetti = null;
      this.confettiDisplay = null;
    }

    if (this.endGameOverlay) {
      this.endGameOverlay.destroy();
      this.endGameOverlay = null;
    }

    if (this.certificateOverlay) {
      this.certificateOverlay.destroy();
      this.certificateOverlay = null;
    }

    if (this.zoomtween) {
      this.zoomtween.stop();
      this.game.tweens.remove(this.zoomtween);
    }

    if (this.tutorial) {
      this.tutorial.destroy(true);
      this.tutorial.onComplete.remove(this.handleTutorialComplete, this);
      this.tutorial = null;
    }

    this.triggerTransition = false;
    this.game.world.setBounds(0, 0, this.game.width, this.game.height);
    this.isZoomedIn = false;

    this.mapContainer.destroy(true);

    this.hud.destroy(true);

    this.hud = null;
    // clear up
    this.container = null;
  }
}



// WEBPACK FOOTER //
// ./js/app/states/WorldMap.js